/**
 * BlogContent — Renders markdown blog content with rich visual styling.
 *
 * Features:
 * - Custom callout boxes via blockquote syntax: > [!tip], > [!experiment], > [!age:4-7], > [!fact], > [!question]
 * - Subject-colored section dividers (hr)
 * - Styled experiment cards (bold "Hva trenger du:" / "Slik gjør du det:" patterns)
 * - Visual blockquotes with teal accent
 * - Responsive tables with subject-colored headers
 */

import React, { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { BlogCallout, type CalloutType } from './BlogCallout';
import { ExperimentIllustration } from './ExperimentIllustration';
import { ExperimentStepper } from './ExperimentStepper';
import type { BlogCategory } from '../../types/blog';
import { CATEGORY_CONFIG } from '../../types/blog';

interface BlogContentProps {
  content: string;
  category?: BlogCategory;
}

// Parse callout syntax from blockquote children
// Format: > [!type] content  or  > [!type:param] content
function parseCallout(children: ReactNode): { type: CalloutType | 'illustration' | 'steps'; param?: string; content: ReactNode } | null {
  // Extract text content from children
  const childArray = Array.isArray(children) ? children : [children];

  for (const child of childArray) {
    if (child && typeof child === 'object' && 'props' in child) {
      const text = extractText(child);
      const match = text.match(/^\[!(tip|experiment|age|fact|question|answer|illustration|steps)(?::([^\]]*))?\]\s*/);
      if (match) {
        const type = match[1] as CalloutType | 'illustration' | 'steps';
        const param = match[2];
        // Return the content without the callout prefix
        const cleanedContent = removeCalloutPrefix(child, match[0]);
        return { type, param, content: cleanedContent };
      }
    }
  }
  return null;
}

function extractText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (node && typeof node === 'object' && 'props' in node) {
    const el = node as React.ReactElement<{ children?: ReactNode }>;
    const children = el.props.children;
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) return children.map(extractText).join('');
  }
  return '';
}

function removeCalloutPrefix(node: ReactNode, prefix: string): ReactNode {
  if (node && typeof node === 'object' && 'props' in node) {
    const el = node as React.ReactElement<{ children?: ReactNode }>;
    const children = el.props.children;
    if (typeof children === 'string') {
      return children.replace(prefix, '');
    }
    if (Array.isArray(children)) {
      let removed = false;
      const newChildren = children.map((c: ReactNode) => {
        if (!removed && typeof c === 'string' && c.includes(prefix.trim())) {
          removed = true;
          return c.replace(prefix.trim(), '');
        }
        if (!removed) {
          const result = removeCalloutPrefix(c, prefix);
          if (result !== c) removed = true;
          return result;
        }
        return c;
      });
      return newChildren;
    }
  }
  return node;
}

// The ONLY blog route is /undring/:slug (see src/App.tsx). The historical
// authoring convention linked posts as /{pillar}/{slug} (e.g. /realfag/...),
// but no such route exists — the catch-all redirects them to the homepage,
// silently killing internal links. Rewrite any legacy pillar-prefixed internal
// link to /undring/{slug} at render time so the whole catalogue resolves
// correctly (and future posts authored this way self-heal too).
const LEGACY_BLOG_PREFIXES = new Set([
  'realfag',
  'sesong-steam',
  'kreativ-laering',
  'digital-foreldre',
  'laereplanen',
  'blog',
]);

function rewriteInternalHref(href: string): string {
  const match = href.match(/^\/([^/?#]+)\/(.+)$/);
  if (match && LEGACY_BLOG_PREFIXES.has(match[1])) {
    return `/undring/${match[2]}`;
  }
  return href;
}

// Build components with category context for colored elements
function buildComponents(category?: BlogCategory): Components {
  const catColor = category ? CATEGORY_CONFIG[category].color : '#00C2A8';

  return {
    h2: ({ children, ...props }) => (
      <h2
        className="font-display font-bold text-ink text-2xl md:text-3xl mt-14 mb-5 scroll-mt-20 flex items-center gap-3"
        {...props}
      >
        <span
          className="inline-block w-1.5 h-8 rounded-full flex-shrink-0"
          style={{ backgroundColor: catColor }}
          aria-hidden="true"
        />
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3
        className="font-display font-semibold text-ink text-xl md:text-2xl mt-8 mb-3"
        {...props}
      >
        {children}
      </h3>
    ),
    h4: ({ children, ...props }) => (
      <h4 className="font-display font-semibold text-ink text-lg mt-6 mb-2" {...props}>
        {children}
      </h4>
    ),
    p: ({ children, ...props }) => {
      // Detect "STEAM-læring:" pattern for learning outcome pills
      const text = extractText(children);
      if (text.startsWith('STEAM-læring:') || text.startsWith('STEAM learning:')) {
        const topics = text.replace(/^STEAM-læring:\s*|^STEAM learning:\s*/, '').split(',').map(s => s.trim());
        return (
          <div className="flex flex-wrap gap-2 mb-6 mt-2">
            <span className="font-display font-semibold text-xs uppercase tracking-wider text-ink-muted mr-1 self-center">
              STEAM
            </span>
            {topics.map((topic, i) => (
              <span
                key={i}
                className="px-2.5 py-1 rounded-full text-xs font-display font-medium text-white"
                style={{ backgroundColor: catColor, opacity: 0.85 }}
              >
                {topic}
              </span>
            ))}
          </div>
        );
      }

      return (
        <p className="font-body text-ink text-base md:text-lg leading-relaxed mb-6" {...props}>
          {children}
        </p>
      );
    },
    a: ({ children, href, ...props }) => {
      const linkClass =
        'text-teal hover:text-teal-dark underline decoration-teal/30 hover:decoration-teal transition-colors';
      const isExternal =
        !!href &&
        (/^(https?:)?\/\//.test(href) || href.startsWith('mailto:') || href.startsWith('tel:'));

      // Internal app links — rewrite legacy pillar prefixes and navigate via the
      // router (SPA, no full-page reload).
      if (href && href.startsWith('/') && !isExternal) {
        return (
          <Link to={rewriteInternalHref(href)} className={linkClass} {...props}>
            {children}
          </Link>
        );
      }

      return (
        <a
          href={href}
          className={linkClass}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          {...props}
        >
          {children}
        </a>
      );
    },
    ul: ({ children, ...props }) => (
      <ul className="list-disc list-outside pl-6 mb-6 space-y-2 font-body text-ink" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol className="list-decimal list-outside pl-6 mb-6 space-y-2 font-body text-ink" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li className="text-base md:text-lg leading-relaxed" {...props}>
        {children}
      </li>
    ),
    blockquote: ({ children, ...props }) => {
      // Try to parse as callout
      const callout = parseCallout(children);
      if (callout) {
        // Illustration: render SVG animation
        if (callout.type === 'illustration' && callout.param) {
          return <ExperimentIllustration type={callout.param} />;
        }

        // Steps: render interactive stepper
        if (callout.type === 'steps') {
          const text = extractText(callout.content);
          const steps = text
            .split(/\d+\.\s+/)
            .map(s => s.trim())
            .filter(Boolean);
          return (
            <ExperimentStepper
              steps={steps}
              categoryColor={catColor}
            />
          );
        }

        return (
          <BlogCallout type={callout.type as CalloutType} ageRange={callout.param}>
            {callout.content}
          </BlogCallout>
        );
      }

      // Default styled blockquote
      return (
        <blockquote
          className="border-l-4 pl-6 py-3 my-8 rounded-r-xl italic font-body text-ink-muted"
          style={{ borderColor: catColor, backgroundColor: `${catColor}08` }}
          {...props}
        >
          {children}
        </blockquote>
      );
    },
    table: ({ children, ...props }) => (
      <div className="overflow-x-auto my-8 rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-sm font-display" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead
        className="text-white font-semibold"
        style={{ backgroundColor: catColor }}
        {...props}
      >
        {children}
      </thead>
    ),
    th: ({ children, ...props }) => (
      <th className="px-4 py-3 text-left border-b border-gray-200" {...props}>
        {children}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td className="px-4 py-3 border-b border-gray-100 text-ink-muted" {...props}>
        {children}
      </td>
    ),
    code: ({ children, className, ...props }) => {
      const isInline = !className;
      if (isInline) {
        return (
          <code
            className="bg-sand px-1.5 py-0.5 rounded text-sm font-mono text-teal-dark"
            {...props}
          >
            {children}
          </code>
        );
      }
      return (
        <code
          className="block bg-navy text-chalk p-4 rounded-xl overflow-x-auto text-sm font-mono my-6 shadow-sm"
          {...props}
        >
          {children}
        </code>
      );
    },
    pre: ({ children }) => <>{children}</>,
    hr: () => (
      <div className="my-12 flex items-center justify-center gap-3">
        <div className="h-px flex-1 bg-gray-200" />
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: catColor }}
        />
        <div
          className="w-3 h-3 rounded-full opacity-60"
          style={{ backgroundColor: catColor }}
        />
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: catColor }}
        />
        <div className="h-px flex-1 bg-gray-200" />
      </div>
    ),
    img: ({ src, alt, ...props }) => (
      <figure className="my-8">
        <img
          src={src}
          alt={alt}
          className="rounded-xl w-full shadow-card"
          loading="lazy"
          {...props}
        />
        {alt && (
          <figcaption className="text-center text-sm text-ink-faint mt-3 font-display">
            {alt}
          </figcaption>
        )}
      </figure>
    ),
    strong: ({ children, ...props }) => (
      <strong className="font-semibold text-ink" {...props}>{children}</strong>
    ),
    em: ({ children, ...props }) => (
      <em className="italic" {...props}>{children}</em>
    ),
  };
}

export function BlogContent({ content, category }: BlogContentProps) {
  const components = buildComponents(category);

  // Strip <!-- INTERACTIVE ... --> comment blocks from content
  // These are metadata for the animation layer, not meant to be displayed
  const cleanedContent = content.replace(/<!--\s*INTERACTIVE[\s\S]*?-->/g, '');

  return (
    <article className="max-w-3xl mx-auto prose-good-atoms">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {cleanedContent}
      </ReactMarkdown>
    </article>
  );
}
